import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { useToast } from '../../hooks/use-toast';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { Bot, Send, User, Sparkles, CheckCircle, Clock, Brain, Zap } from 'lucide-react';

const AIChatTester = ({ userType = 'customer' }) => {
  const { toast } = useToast();
  const { makeAuthenticatedRequest } = useCRMAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [autoResolutionEnabled, setAutoResolutionEnabled] = useState(true);
  const [detectedIntent, setDetectedIntent] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Initial greeting
    const greeting = userType === 'customer'
      ? "Hello! I'm your AI assistant. How can I help you today?"
      : "AI Chat Agent Test Interface. Send test messages to see auto-resolution in action.";
    
    addMessage('ai', greeting);
  }, [userType]);

  const addMessage = (sender, text, metadata = {}) => {
    setMessages(prev => [...prev, {
      id: Date.now() + Math.random(),
      sender,
      text,
      timestamp: new Date().toLocaleTimeString(),
      ...metadata
    }]);
  };

  // REAL AI intent detection — calls the backend LLM intent classifier (was a client-side keyword
  // matcher with fabricated responses like "$150 balance"). Returns real intent/confidence/reasoning.
  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    addMessage('user', userMessage);
    setIsProcessing(true);

    try {
      const resp = await makeAuthenticatedRequest('/api/v1/ai-agent/detect-intent', {
        method: 'POST',
        body: JSON.stringify({
          message: userMessage,
          conversation_history: messages
            .filter(m => m.sender === 'user' || m.sender === 'ai')
            .slice(-6)
            .map(m => ({ role: m.sender === 'ai' ? 'assistant' : 'user', content: m.text })),
          customer_context: {}
        })
      });
      if (!resp.ok) throw new Error(`intent detection failed (${resp.status})`);
      const data = await resp.json();

      const intentName = data.intent || 'unknown';
      const confidence = typeof data.confidence === 'number' ? data.confidence : null;
      const requiresEscalation = data.requires_escalation === true;
      setDetectedIntent({ name: intentName, confidence, escalate: requiresEscalation });

      // Honest AI output: show the model's real reasoning + suggested category (no fabricated reply).
      const parts = [];
      if (data.reasoning) parts.push(data.reasoning);
      if (data.suggested_category) parts.push(`Suggested category: ${data.suggested_category}`);
      parts.push(requiresEscalation
        ? 'This query would be routed to a human agent.'
        : 'This query could be handled by AI auto-resolution.');

      addMessage('ai', parts.join('\n\n'), {
        intent: intentName,
        confidence,
        autoResolved: !requiresEscalation,
        escalated: requiresEscalation
      });

      if (autoResolutionEnabled) {
        const pct = confidence != null ? ` (${Math.round(confidence * 100)}% confidence)` : '';
        if (requiresEscalation) {
          toast({ title: '⚠ Escalation recommended', description: `Intent: ${intentName}${pct}`, variant: 'destructive' });
        } else {
          toast({ title: '✓ Intent detected', description: `${intentName}${pct}` });
        }
      }
    } catch (e) {
      console.error('AI intent detection error:', e);
      addMessage('ai', 'AI intent detection is unavailable right now. Please try again in a moment.', { error: true });
      toast({ title: 'AI unavailable', description: 'Could not reach the AI intent service.', variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleQuickTest = (testType) => {
    const testQueries = {
      'simple': "What are your business hours?",
      'moderate': "I forgot my password, can you help me reset it?",
      'complex': "I've been charged twice and the app keeps crashing",
      'multilingual': "Bonjour, j'ai besoin d'aide avec ma facture"
    };

    setInputMessage(testQueries[testType]);
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-white via-green-50 to-purple-50">
      {/* Header */}
      <div className="p-6 bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Bot className="h-6 w-6 text-purple-600" />
              AI Chat Agent Tester
            </h1>
            <p className="text-gray-600">Test auto-resolution capabilities with real-time intent detection</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Auto-Resolution:</span>
              <Button
                variant={autoResolutionEnabled ? "default" : "outline"}
                size="sm"
                onClick={() => setAutoResolutionEnabled(!autoResolutionEnabled)}
              >
                {autoResolutionEnabled ? 'Enabled' : 'Disabled'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 p-6 grid lg:grid-cols-3 gap-6 overflow-hidden">
        {/* Chat Area */}
        <div className="lg:col-span-2 flex flex-col">
          <Card className="flex-1 flex flex-col">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Chat Conversation</span>
                {detectedIntent && (
                  <Badge variant={detectedIntent.autoResolved ? "default" : "destructive"}>
                    {detectedIntent.name} ({(detectedIntent.confidence * 100).toFixed(0)}%)
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col overflow-hidden">
              <ScrollArea className="flex-1 pr-4">
                <div className="space-y-4">
                  {messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] ${msg.sender === 'user' ? 'order-2' : 'order-1'}`}>
                        <div className="flex items-start gap-2 mb-1">
                          {msg.sender === 'ai' && <Bot className="h-5 w-5 text-purple-600 mt-1" />}
                          {msg.sender === 'user' && <User className="h-5 w-5 text-blue-600 mt-1" />}
                          <div className={`flex-1 rounded-lg p-3 ${
                            msg.sender === 'user'
                              ? 'bg-blue-100 text-blue-900'
                              : 'bg-purple-50 text-purple-900'
                          }`}>
                            <p className="text-sm">{msg.text}</p>
                            <div className="flex items-center gap-2 mt-2 text-xs opacity-70">
                              <Clock className="h-3 w-3" />
                              {msg.timestamp}
                              {msg.autoResolved && (
                                <>
                                  <CheckCircle className="h-3 w-3 text-green-600" />
                                  <span className="text-green-600">Auto-Resolved</span>
                                </>
                              )}
                              {msg.escalated && (
                                <>
                                  <Zap className="h-3 w-3 text-orange-600" />
                                  <span className="text-orange-600">Escalated</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {isProcessing && (
                    <div className="flex items-center gap-2 text-gray-500">
                      <Bot className="h-5 w-5 animate-pulse" />
                      <span className="text-sm">AI is thinking...</span>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Input */}
              <div className="mt-4 flex gap-2">
                <Input
                  placeholder="Type your message..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  disabled={isProcessing}
                />
                <Button 
                  onClick={handleSendMessage}
                  disabled={isProcessing || !inputMessage.trim()}
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Test Scenarios */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Quick Test Scenarios</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                className="w-full justify-start text-sm"
                onClick={() => handleQuickTest('simple')}
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Simple Query
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start text-sm"
                onClick={() => handleQuickTest('moderate')}
              >
                <Brain className="h-4 w-4 mr-2" />
                Moderate Query
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start text-sm"
                onClick={() => handleQuickTest('complex')}
              >
                <Zap className="h-4 w-4 mr-2" />
                Complex Issue
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start text-sm"
                onClick={() => handleQuickTest('multilingual')}
              >
                🌍 Multilingual Test
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Auto-Resolution Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Messages:</span>
                <Badge>{messages.filter(m => m.sender === 'user').length}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Auto-Resolved:</span>
                <Badge variant="default">
                  {messages.filter(m => m.autoResolved).length}
                </Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Escalated:</span>
                <Badge variant="destructive">
                  {messages.filter(m => m.escalated).length}
                </Badge>
              </div>
              <div className="pt-3 border-t">
                <div className="text-sm text-gray-600 mb-2">Success Rate:</div>
                <div className="text-2xl font-bold text-green-600">
                  {messages.filter(m => m.sender === 'user').length > 0
                    ? Math.round((messages.filter(m => m.autoResolved).length / messages.filter(m => m.sender === 'user').length) * 100)
                    : 0}%
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AIChatTester;
